import { useNavigate } from "react-router-dom";
import { canManageReport } from "@/profitability/access";
import {
  profitabilityNewRunPath,
  profitabilityRunPath,
  profitabilityTemplateSettingsPath,
} from "@/profitability/routes";
import type {
  ProfitabilityReport,
  ProfitabilityRun,
} from "@/profitability/types";
import { useAgentNav } from "./use-agent-nav";

/**
 * What an empty Review, Insights or Workbook can offer, so each says the same
 * thing in the same words. An action is absent for someone who may not take
 * it: running a period and changing the agent are finance's
 * (Profitability::Access), and an archived agent runs nothing.
 */
export interface EmptyActions {
  /** Upload the files for a new period. */
  run?: () => void;
  /** Edit agent, `draft` waiting in its composer for the owner to finish. */
  change?: (draft?: string) => void;
  /** Settings, where the checks' limits are tuned. */
  limits?: () => void;
  /** Open the period that is building, which shows how far it has got. */
  progress?: () => void;
}

export const useEmptyActions = (
  report: ProfitabilityReport,
  /** The newest run still building, if any. */
  running: ProfitabilityRun | null | undefined,
): EmptyActions => {
  const navigate = useNavigate();
  const nav = useAgentNav(!report.published);
  const manages = canManageReport(report) && !report.archived_at;
  return {
    run:
      manages && report.published
        ? () => navigate(profitabilityNewRunPath(report.id))
        : undefined,
    change: manages && report.published ? nav.edit : undefined,
    limits: manages
      ? () => navigate(profitabilityTemplateSettingsPath(report.id))
      : undefined,
    progress: running
      ? () => navigate(profitabilityRunPath(running.id))
      : undefined,
  };
};
